import { ReactNode } from 'react';
import { Box, Container, Typography, alpha, Stack, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import { ArrowRightOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { ArtAssessment, ArtSupportPlan, ArtDraft, ArtCheck, ArtAlert, ArtShield } from './SolveTileArt';
import { SECTION_PY } from 'const/section';

// ==============================|| SOLVE SECTION ||============================== //
//
// 構成は「悩みの提示 → お任せくださいの受け → 解決の流れ3枚」の順。
//
// 各カードの右側は [before] → [after] の2コマで流れを見せている。実際の画面キャプチャ
// ではなくアイコンによる図解にしているのは、加算書類の下書き画面やアラート画面の
// キャプチャがまだ用意できていないため。実物と異なるモック画像を置くと誤解を招くので、
// 図解として割り切っている。キャプチャが揃い次第、画像に差し替えてよい。

type Tile = {
  /** コマの中身を表す線画（SolveTileArt） */
  art: JSX.Element;
  /** 枠の中に入れる短いラベル */
  label: string;
  /** 枠の下に置くキャプション */
  caption: ReactNode;
};

type SolveCard = {
  /** 改行を入れる場合は、狭い幅で不自然に折り返さないよう md 以上に限定すること */
  title: ReactNode;
  before: Tile;
  after: Tile;
};

const primaryColor = '#2aa1af';
const accentRed = '#e2574c';

const problems = [
  '個別支援計画書の\n作成業務の負担が大きい',
  '加算が取り切れているか\n分からない',
  '報酬改定の影響で\n報酬が減っている'
];

const cards: SolveCard[] = [
  {
    title: (
      <>
        アセスメントからAIが目標提案、
        <Box component="br" sx={{ display: { xs: 'none', md: 'block' } }} />
        計画書へ自動反映
      </>
    ),
    before: { art: <ArtAssessment />, label: 'アセスメント', caption: '悩みを入力' },
    after: {
      art: <ArtSupportPlan />,
      label: '個別支援計画書',
      caption: (
        <>
          AIが計画書を作成
          <Box component="span" sx={{ display: 'block', color: accentRed, fontWeight: 900, mt: 0.5 }}>
            作成業務時間を大幅に削減！
          </Box>
        </>
      )
    }
  },
  {
    title: <>算定に必要な書類作成をAIが下書き、音声入力にも対応</>,
    before: { art: <ArtDraft />, label: '加算書類の下書き', caption: 'AIが下書き' },
    after: { art: <ArtCheck />, label: '内容を確認', caption: '確認するだけ！' }
  },
  {
    title: <>支援計画書の更新、支援プログラム公表、BCP策定の期限をアラート</>,
    before: { art: <ArtAlert />, label: '期限が近づくと通知', caption: '必要書類をアラート' },
    after: { art: <ArtShield />, label: '減算なし', caption: '減算を確実に防ぎます！' }
  }
];

const SolveSection = () => {
  /** [before] / [after] の1コマ */
  const renderTile = (tile: Tile) => (
    <Stack spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
      <Box
        aria-hidden="true"
        sx={{
          width: '100%',
          aspectRatio: '4 / 3',
          maxWidth: 200,
          borderRadius: '16px',
          border: `2px solid ${alpha(primaryColor, 0.35)}`,
          bgcolor: alpha(primaryColor, 0.04),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          px: 1.5
        }}
      >
        <Box sx={{ width: '64%', maxWidth: 124, display: 'flex' }}>{tile.art}</Box>
        <Typography
          component="span"
          sx={{
            color: primaryColor,
            fontWeight: 800,
            fontSize: { xs: '0.7rem', md: '0.8rem' },
            textAlign: 'center',
            lineHeight: 1.4
          }}
        >
          {tile.label}
        </Typography>
      </Box>
      <Typography
        component="p"
        sx={{
          width: '100%',
          color: '#374151',
          fontWeight: 800,
          fontSize: { xs: '0.8rem', md: '0.9rem' },
          textAlign: 'center',
          lineHeight: 1.6
        }}
      >
        {tile.caption}
      </Typography>
    </Stack>
  );

  return (
    <Box component="section">
      {/* ============ 悩みの提示 ============ */}
      <Box sx={{ bgcolor: '#fff', pt: SECTION_PY, pb: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '1.9rem', md: '2.8rem' },
                color: '#1a1a1a',
                lineHeight: 1.4,
                textAlign: 'center',
                mb: { xs: 5, md: 7 }
              }}
            >
              児発・放デイ運営の、
              <br />
              こんなお悩みありませんか？
            </Typography>
          </motion.div>

          <Grid container spacing={2.5} alignItems="stretch">
            {problems.map((problem, index) => (
              <Grid item xs={12} md={4} key={problem}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  style={{ height: '100%' }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      py: { xs: 3, md: 3.5 },
                      px: 2.5,
                      borderRadius: '16px',
                      bgcolor: '#f1f3f3',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      component="p"
                      sx={{
                        color: '#4b5563',
                        fontWeight: 800,
                        fontSize: { xs: '1rem', md: '1.08rem' },
                        lineHeight: 1.7,
                        textAlign: 'center',
                        whiteSpace: 'pre-line'
                      }}
                    >
                      {problem}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ============ 受けの帯 ============ */}
      <Box
        sx={{
          bgcolor: alpha(primaryColor, 0.1),
          py: { xs: 3.5, md: 4.5 },
          position: 'relative'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            component="p"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '1.25rem', md: '1.8rem' },
              color: '#1a1a1a',
              textAlign: 'center',
              lineHeight: 1.5
            }}
          >
            <Box component="span" sx={{ color: primaryColor }}>OHANA AI</Box> にお任せください！
          </Typography>
        </Container>

        {/* 下向きの三角。次のセクションへ視線を送る */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            left: '50%',
            bottom: 0,
            transform: 'translate(-50%, 100%)',
            width: 0,
            height: 0,
            borderLeft: '28px solid transparent',
            borderRight: '28px solid transparent',
            borderTop: `22px solid ${alpha(primaryColor, 0.1)}`
          }}
        />
      </Box>

      {/* ============ 解決の流れ ============ */}
      <Box sx={{ bgcolor: '#f7fbfb', pt: { xs: 8, md: 11 }, pb: SECTION_PY }}>
        <Container maxWidth="lg">
          <Stack spacing={{ xs: 3, md: 4 }}>
            {cards.map((card, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.12 }}
              >
                <Box
                  component="article"
                  sx={{
                    p: { xs: 3.5, md: 5 },
                    borderRadius: '24px',
                    bgcolor: '#fff',
                    border: `1px solid ${alpha(primaryColor, 0.12)}`,
                    boxShadow: `0 12px 34px ${alpha(primaryColor, 0.06)}`
                  }}
                >
                  <Grid container spacing={{ xs: 4, md: 5 }} alignItems="center">
                    {/* 左：見出し */}
                    <Grid item xs={12} md={5}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Typography
                          aria-hidden="true"
                          component="span"
                          sx={{
                            fontFamily: 'serif',
                            fontWeight: 900,
                            fontSize: { xs: '2.2rem', md: '2.8rem' },
                            color: alpha(primaryColor, 0.25),
                            lineHeight: 1,
                            flexShrink: 0
                          }}
                        >
                          {index + 1}
                        </Typography>
                        <Typography
                          variant="h3"
                          component="h3"
                          sx={{
                            fontWeight: 800,
                            fontSize: { xs: '1.15rem', md: '1.4rem' },
                            color: '#1a1a1a',
                            lineHeight: 1.6
                          }}
                        >
                          {card.title}
                        </Typography>
                      </Stack>
                    </Grid>

                    {/* 右：before → after の図解 */}
                    <Grid item xs={12} md={7}>
                      <Stack
                        direction="row"
                        spacing={{ xs: 1.5, md: 3 }}
                        alignItems="flex-start"
                        justifyContent="center"
                      >
                        {renderTile(card.before)}

                        <Box
                          aria-hidden="true"
                          sx={{
                            color: primaryColor,
                            fontSize: { xs: '1.1rem', md: '1.5rem' },
                            display: 'flex',
                            alignItems: 'center',
                            // 枠の高さの中心あたりに矢印が来るように少し下げる
                            pt: { xs: 4, md: 6 },
                            flexShrink: 0
                          }}
                        >
                          <ArrowRightOutlined />
                        </Box>

                        {renderTile(card.after)}
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              </motion.div>
            ))}
          </Stack>

          {/* 画面下部へ視線を送る装飾 */}
          <Box aria-hidden="true" sx={{ textAlign: 'center', mt: { xs: 5, md: 6 }, color: alpha(primaryColor, 0.35), fontSize: '1.4rem' }}>
            <ArrowDownOutlined />
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default SolveSection;
